import { forwardRef } from 'preact/compat';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { logNavInteraction, NAV_LOCATIONS, NAV_SECTIONS, NAV_ELEMENT_TYPES, NAV_ACTIONS } from 'utils';
import { useState, useEffect } from 'preact/hooks';

const BASE_NAV_INTERACTION_CONFIG = {
  location: NAV_LOCATIONS.PRIMARY,
  depth: 1,
};

const NavDrawer = forwardRef(({ isOpen, onOpenSubDrawer, onCloseNavDrawer, customer, menuItems }, ref) => {
  const [isFullyVisible, setIsFullyVisible] = useState(false);

  // Navigation Items
  const navItems = Object.values(menuItems).map(item => {
    const isSimpleLink =
      (!item.category_groups || item.category_groups.length === 0) &&
      (!item.promo_cards || item.promo_cards.length === 0) &&
      (!item.featured_links || item.featured_links.length === 0) &&
      (!item.featured_products || item.featured_products.length === 0);
  
    return {
      text: item.label,
      href: item.link,
      featuredProducts: item.featured_products,
      dropdownContent: item.category_groups,
      featuredPromoCards: item.promo_cards,
      displayNewsletter: item.display_newsletter,
      featuredLinks: item.featured_links,
      isNew: item.is_new,
      isSimpleLink,
    };
  });
  


  const closeNavDrawer = function(navInteractionConfig) {
    onCloseNavDrawer();

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  }

  const openSubDrawer = function(item, navInteractionConfig) {
    if (!item.isSimpleLink) {
      onOpenSubDrawer(item.text);
    }

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  }

  useEffect(() => {
    if (isOpen) {
      setIsFullyVisible(true);
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      const timeout = setTimeout(() => setIsFullyVisible(false), 500);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';

      return () => {
        clearTimeout(timeout);
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isOpen]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Main menu"
      aria-hidden={!isOpen}
      ref={ref}
      className={`tw-fixed tw-right-0 tw-top-0 tw-z-50 tw-flex tw-h-dvh tw-w-full tw-flex-col tw-overflow-hidden tw-transition-opacity tw-duration-500 ${
        isFullyVisible ? 'tw-visible tw-opacity-100' : 'tw-invisible tw-opacity-0'
      }`}
    >
      {/* Background Overlay */}
      <div
        className={`tw-absolute tw-h-screen tw-w-full tw-bg-[rgba(63,68,67,0.7)] tw-transition-opacity tw-duration-500 ${
          isOpen ? 'tw-visible tw-opacity-100' : 'tw-invisible tw-opacity-0'
        }`}
        onClick={() => closeNavDrawer({
          section: NAV_SECTIONS.PRIMARY.MOBILE.MENU_TOGGLE, 
          order: 1, 
          elementType: NAV_ELEMENT_TYPES.BACKDROP, 
          text: 'Close',
          action: NAV_ACTIONS.CLOSE_SUBMENU,
        })}
      ></div>

      {/* Drawer Content */}
      <div
        className={`tw-flex tw-h-screen tw-min-h-dvh tw-w-[300px] tw-transform tw-flex-col tw-self-end tw-bg-white tw-px-8 tw-pt-8 tw-transition-transform tw-duration-300 ${
        isOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'
        }`}
      >
        <div className="tw-flex tw-w-full tw-items-center tw-justify-end">
          <div className='tw-p-10 tw-text-[#3F4443]'>
            <XMarkIcon className='tw-size-24'onClick={() => closeNavDrawer({
              section: NAV_SECTIONS.PRIMARY.MOBILE.MENU_TOGGLE, 
              order: 1, 
              elementType: NAV_ELEMENT_TYPES.ICON, 
              text: 'Close',
              action: NAV_ACTIONS.CLOSE_SUBMENU,
            })} />
          </div>
        </div>
        <div className='tw-flex tw-h-full tw-flex-col tw-gap-24 tw-px-8'>
        <ul className="tw-mt-24 tw-flex tw-flex-col tw-gap-4">
            {navItems.map((item, index) => (
            <li
              key={index}
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-self-stretch tw-text-center tw-text-14 tw-font-semibold tw-leading-16 tw-text-[#3F4443]"
              onClick={() => openSubDrawer(item, {
                section: NAV_SECTIONS.PRIMARY.MOBILE.MAIN, 
                order: index + 1, 
                elementType: NAV_ELEMENT_TYPES.TEXT, 
                text: item.text,
                action: NAV_ACTIONS.OPEN_SUBMENU,
              })}
            >
              <div className='tw-flex tw-w-full tw-items-center tw-text-left'>
                {item.href ? (
                  <a
                    href={item.href}
                    className="tw-block tw-w-full tw-py-[20px] tw-text-16 tw-leading-18"
                    onClick={() => logNavInteraction({
                      ...BASE_NAV_INTERACTION_CONFIG,
                      section: NAV_SECTIONS.PRIMARY.MOBILE.MAIN, 
                      order: index + 1, 
                      elementType: NAV_ELEMENT_TYPES.TEXT, 
                      text: item.text,
                      action: NAV_ACTIONS.LINK,
                    })}
                  >
                    {item.text}
                  </a>
                ) : (
                  <span className="tw-block tw-py-[20px] tw-text-16 tw-leading-18">{item.text}</span>
                )}
                {item.isNew && (
                        <span className="tw--top-[0.15rem] tw-ml-8 tw-text-12 tw-font-semibold tw-uppercase tw-leading-[14px] tw-text-[#00A3E0]">
                        *New*
                      </span>
                )}
              </div>
              {!item.isSimpleLink && <ChevronRightIcon className="tw-size-[16px] tw-stroke-[#3F4443]" />}
            </li>
          ))}
        </ul>
        <ul className="tw-mt-auto tw-flex tw-flex-col">
          <li className="tw-self-stretch tw-border-t tw-border-[#F3F3F7] tw-py-[24px] tw-pl-16 tw-pr-[.75rem] tw-text-center tw-text-14 tw-font-semibold tw-leading-16 tw-text-[#3F4443]">
            {customer ? (
              <div className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between">
                <a href={'/pages/account-plan'} onClick={() => logNavInteraction({
                  ...BASE_NAV_INTERACTION_CONFIG,
                  section: NAV_SECTIONS.PRIMARY.MOBILE.BOTTOM_LINKS, 
                  order: 1, 
                  elementType: NAV_ELEMENT_TYPES.BUTTON, 
                  text: 'My Account',
                  action: NAV_ACTIONS.LINK,
                })}>
                  My Account</a>
                <a className="tw-text-[#B91C1C]" href="/account/logout" onClick={() => logNavInteraction({
                  ...BASE_NAV_INTERACTION_CONFIG,
                  section: NAV_SECTIONS.PRIMARY.MOBILE.BOTTOM_LINKS, 
                  order: 2, 
                  elementType: NAV_ELEMENT_TYPES.BUTTON, 
                  text: 'Sign Out',
                  action: NAV_ACTIONS.LINK,
                })}>
                  Sign Out
                </a> 
            </div>
            ): (
            <div className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between">
             <a className="tw-text-[##3F4443]" href="/account/login" onClick={() => logNavInteraction({
                ...BASE_NAV_INTERACTION_CONFIG,
                section: NAV_SECTIONS.PRIMARY.MOBILE.BOTTOM_LINKS, 
                order: 1, 
                elementType: NAV_ELEMENT_TYPES.BUTTON, 
                text: 'Sign In',
                action: NAV_ACTIONS.LINK,
              })}>
                Sign In
              </a>
              <a className="tw-text-[##3F4443]" href="/account/register"  onClick={() => logNavInteraction({
                ...BASE_NAV_INTERACTION_CONFIG,
                section: NAV_SECTIONS.PRIMARY.MOBILE.BOTTOM_LINKS, 
                order: 2, 
                elementType: NAV_ELEMENT_TYPES.BUTTON, 
                text: 'Create an Account',
                action: NAV_ACTIONS.LINK,
              })}>
                Create an Account
              </a>
              </div>
            )}
          </li>
          <li className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-self-stretch tw-border-t tw-border-[#F3F3F7] tw-py-[24px] tw-pl-16 tw-text-center tw-text-14 tw-font-semibold tw-leading-16 tw-text-[#3F4443]">
            <a href="/pages/faq" onClick={() => logNavInteraction({
              ...BASE_NAV_INTERACTION_CONFIG,
              section: NAV_SECTIONS.PRIMARY.MOBILE.BOTTOM_LINKS, 
              order: 3, 
              elementType: NAV_ELEMENT_TYPES.BUTTON, 
              text: 'Help',
              action: NAV_ACTIONS.LINK,
            })}>
              Help
            </a>
          </li>
        </ul>
        </div>
      </div>
    </div>
  );
});

export default NavDrawer;
